import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NavigationHeader from '../components/NavigationHeader';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import postService from '../services/postService';
import { Share2, Check, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const pageSize = 12;

const PostsFeed = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [copiedPostId, setCopiedPostId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pageData, setPageData] = useState({ content: [], totalPages: 0, totalElements: 0, number: 0 });
  const [highlightedPost, setHighlightedPost] = useState(null);

  const q = searchParams.get('q') || '';
  const category = searchParams.get('category') || '';
  const shopSlug = searchParams.get('shop') || '';
  const featured = searchParams.get('featured');
  const withImages = searchParams.get('withImages') === 'true';
  const sort = searchParams.get('sort') || 'best';
  const page = parseInt(searchParams.get('page') || '0', 10);
  const postId = searchParams.get('postId');

  const featuredBool = useMemo(() => {
    if (featured === 'true') return true;
    if (featured === 'false') return false;
    return undefined;
  }, [featured]);

  const rtf = useMemo(() => new Intl.RelativeTimeFormat(i18n.language || 'en', { numeric: 'auto' }), [i18n.language]);
  const formatRelativeTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = Date.now();
    let diff = Math.round((d.getTime() - now) / 1000); // seconds
    const units = [
      ['year', 60 * 60 * 24 * 365],
      ['month', 60 * 60 * 24 * 30],
      ['week', 60 * 60 * 24 * 7],
      ['day', 60 * 60 * 24],
      ['hour', 60 * 60],
      ['minute', 60],
      ['second', 1],
    ];
    for (const [unit, sec] of units) {
      if (Math.abs(diff) >= sec || unit === 'second') {
        const value = Math.round(diff / sec);
        return rtf.format(value, unit);
      }
    }
    return '';
  };

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await postService.searchPublicPosts({ q, category, shopSlug, featured: featuredBool, withImages, sort, page, size: pageSize });
      setPageData(data);
      
      if (postId) {
        const highlightPost = data.content.find(p => p.id === parseInt(postId, 10));
        if (highlightPost) {
          setHighlightedPost(highlightPost);
          // Track view when someone opens a shared link
          postService.incrementViewCount(highlightPost.id);
          setTimeout(() => {
            const el = document.getElementById(`post-${postId}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }
      }
    } catch (e) {
      setError(t('postsFeed.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, shopSlug, featured, withImages, sort, page, postId]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === '' || value === undefined || value === null) {
      next.delete(key);
    } else {
      next.set(key, String(value));
    }
    next.delete('page');
    setSearchParams(next);
  };

  const onSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const query = formData.get('q');
    updateParam('q', query);
  };

  const goToPage = (p) => updateParam('page', p);

  const sharePost = (post) => {
    const url = `${window.location.origin}/posts?postId=${post.id}`;
    navigator.clipboard.writeText(url).then(() => {
      // Track share count
      postService.incrementShareCount(post.id);
      setCopiedPostId(post.id);
      toast.success(t('postsFeed.linkCopied', 'Link copied to clipboard!'));
      setTimeout(() => setCopiedPostId(null), 2000);
    }).catch(() => {
      toast.error(t('postsFeed.linkCopyFailed', 'Failed to copy link'));
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <NavigationHeader />
      <div className="max-w-[680px] mx-auto px-4 pt-24 pb-12">
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">{t('postsFeed.title')}</h1>
          <p className="text-gray-400 text-base">{t('postsFeed.subtitle')}</p>
        </div>

        <div className="mb-6">
          <form onSubmit={onSearch} className="flex gap-2">
            <Input
              name="q"
              defaultValue={q}
              placeholder={t('postsFeed.searchPlaceholder')}
              className="flex-1"
            />
            <Select
              value={sort}
              onChange={(e) => updateParam('sort', e.target.value)}
              className="w-32"
            >
              <option value="best">{t('postsFeed.sort.best')}</option>
              <option value="latest">{t('postsFeed.sort.latest')}</option>
              <option value="featured">{t('postsFeed.sort.featured')}</option>
            </Select>
            <Button type="submit" variant="primary">
              {t('postsFeed.actions.search')}
            </Button>
          </form>
        </div>

        {loading && <div className="py-20 text-center text-gray-500">{t('common.loading')}</div>}
        {error && <div className="py-4 px-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">{error}</div>}        {!loading && !error && (
          <>
            {pageData.totalElements === 0 ? (
              <div className="py-20 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-800/50 mb-4">
                  <MessageCircle size={32} className="text-gray-600" />
                </div>
                <p className="text-gray-500 text-lg">{t('postsFeed.empty')}</p>
              </div>
            ) : (
              <>
                <div className="space-y-0 bg-gray-900/40 backdrop-blur-sm rounded-2xl border border-gray-800/50 overflow-hidden">
                  {highlightedPost && (
                    <div 
                      id={`post-${highlightedPost.id}`}
                      className="group flex gap-3 p-5 bg-[#a15507]/10 border-l-4 border-[#a15507] transition-colors animate-pulse-slow"
                    >
                      {(() => {
                        const shop = highlightedPost.shop || {};
                        const slug = shop.slug || highlightedPost.shopSlug;
                        const avatar = shop.logoUrl;
                        const shopName = shop.name || '';
                        const images = Array.isArray(highlightedPost.images) ? highlightedPost.images : [];
                        return (
                          <>
                            {slug ? (
                              <Link to={`/shop/${slug}`} className="flex-shrink-0">
                                {avatar ? (
                                  <img src={avatar} alt={shopName} className="w-11 h-11 rounded-full object-cover ring-2 ring-[#a15507]" />
                                ) : (
                                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#a15507] to-[#7a3f05] flex items-center justify-center text-white font-semibold text-lg shadow-lg">
                                    {shopName ? shopName.charAt(0).toUpperCase() : '•'}
                                  </div>
                                )}
                              </Link>
                            ) : (
                              <div className="w-11 h-11 rounded-full bg-gray-800/60 flex items-center justify-center text-gray-400 font-semibold">
                                {shopName ? shopName.charAt(0).toUpperCase() : '•'}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                                {shopName && (
                                  slug ? (
                                    <Link to={`/shop/${slug}`} className="text-white font-semibold hover:underline truncate max-w-[200px]">
                                      {shopName}
                                    </Link>
                                  ) : (
                                    <span className="text-white font-semibold truncate max-w-[200px]">{shopName}</span>
                                  )
                                )}
                                {slug && (
                                  <span className="text-gray-500 text-sm truncate">@{slug}</span>
                                )}
                                <span className="text-gray-600">·</span>
                                <span className="text-gray-500 text-sm whitespace-nowrap">{formatRelativeTime(highlightedPost.createdAt)}</span>
                                {highlightedPost.isFeatured && (
                                  <span className="ml-auto text-[11px] font-medium px-2 py-0.5 rounded-full bg-gradient-to-r from-[#a15507] to-[#7a3f05] text-white shadow-sm">
                                    {t('postsFeed.badges.featured')}
                                  </span>
                                )}
                              </div>
                              {highlightedPost.content && (
                                <div className="mt-1.5 text-gray-200 text-[15px] leading-relaxed whitespace-pre-wrap break-words">
                                  {highlightedPost.content}
                                </div>
                              )}
                              {images.length > 0 && (
                                <div className="mt-3">
                                  {images.length === 1 ? (
                                    <img src={images[0]} alt="" className="w-full rounded-2xl border border-gray-800/60 max-h-[500px] object-cover shadow-lg" />
                                  ) : images.length === 2 ? (
                                    <div className="grid grid-cols-2 gap-1.5">
                                      {images.map((img, idx) => (
                                        <img key={idx} src={img} alt="" className="w-full h-60 object-cover rounded-xl border border-gray-800/60" />
                                      ))}
                                    </div>
                                  ) : (
                                    <div className={`grid ${images.length === 3 ? 'grid-cols-2' : 'grid-cols-2'} gap-1.5`}>
                                      {images.slice(0, 4).map((img, idx) => (
                                        <img key={idx} src={img} alt="" className={`w-full ${images.length === 3 && idx === 0 ? 'col-span-2 h-60' : 'h-40'} object-cover rounded-xl border border-gray-800/60`} />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="mt-3 flex items-center gap-1 -ml-2">
                                <button 
                                  onClick={() => sharePost(highlightedPost)}
                                  className="group/btn flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-[#a15507]/10 text-gray-500 hover:text-[#a15507] transition-colors" 
                                  aria-label="Share"
                                >
                                  {copiedPostId === highlightedPost.id ? <Check size={18} className="text-green-400" /> : <Share2 size={18} className="group-hover/btn:scale-110 transition-transform" />}
                                  <span className="text-xs">{copiedPostId === highlightedPost.id ? t('postsFeed.copied', 'Copied') : t('postsFeed.share', 'Share')}</span>
                                </button>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                  {pageData.content.filter(p => !highlightedPost || p.id !== highlightedPost.id).map((post, idx) => {
                    const shop = post.shop || {};
                    const slug = shop.slug || post.shopSlug;
                    const avatar = shop.logoUrl;
                    const shopName = shop.name || '';
                    const images = Array.isArray(post.images) ? post.images : [];
                    return (
                      <div 
                        key={post.id}
                        id={`post-${post.id}`}
                        className={`group flex gap-3 p-5 hover:bg-white/[0.02] transition-colors ${idx > 0 || highlightedPost ? 'border-t border-gray-800/50' : ''}`}
                      >
                        {/* Avatar */}
                        {slug ? (
                          <Link to={`/shop/${slug}`} className="flex-shrink-0">
                            {avatar ? (
                              <img src={avatar} alt={shopName} className="w-11 h-11 rounded-full object-cover ring-2 ring-gray-800/50" />
                            ) : (
                              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#a15507] to-[#7a3f05] flex items-center justify-center text-white font-semibold text-lg shadow-lg">
                                {shopName ? shopName.charAt(0).toUpperCase() : '•'}
                              </div>
                            )}
                          </Link>
                        ) : (
                          <div className="w-11 h-11 rounded-full bg-gray-800/60 flex items-center justify-center text-gray-400 font-semibold">
                            {shopName ? shopName.charAt(0).toUpperCase() : '•'}
                          </div>
                        )}

                        {/* Body */}
                        <div className="flex-1 min-w-0">
                          {/* Header */}
                          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                            {shopName && (
                              slug ? (
                                <Link to={`/shop/${slug}`} className="text-white font-semibold hover:underline truncate max-w-[200px]">
                                  {shopName}
                                </Link>
                              ) : (
                                <span className="text-white font-semibold truncate max-w-[200px]">{shopName}</span>
                              )
                            )}
                            {slug && (
                              <span className="text-gray-500 text-sm truncate">@{slug}</span>
                            )}
                            <span className="text-gray-600">·</span>
                            <span className="text-gray-500 text-sm whitespace-nowrap">{formatRelativeTime(post.createdAt)}</span>
                            {post.isFeatured && (
                              <span className="ml-auto text-[11px] font-medium px-2 py-0.5 rounded-full bg-gradient-to-r from-[#a15507] to-[#7a3f05] text-white shadow-sm">
                                {t('postsFeed.badges.featured')}
                              </span>
                            )}
                          </div>

                          {/* Content */}
                          {post.content && (
                            <div className="mt-1.5 text-gray-200 text-[15px] leading-relaxed whitespace-pre-wrap break-words">
                              {post.content}
                            </div>
                          )}

                          {/* Images */}
                          {images.length > 0 && (
                            <div className="mt-3">
                              {images.length === 1 ? (
                                <img 
                                  src={images[0]} 
                                  alt="" 
                                  className="w-full rounded-2xl border border-gray-800/60 max-h-[500px] object-cover shadow-lg" 
                                />
                              ) : images.length === 2 ? (
                                <div className="grid grid-cols-2 gap-1.5">
                                  {images.map((img, idx) => (
                                    <img 
                                      key={idx} 
                                      src={img} 
                                      alt="" 
                                      className="w-full h-60 object-cover rounded-xl border border-gray-800/60" 
                                    />
                                  ))}
                                </div>
                              ) : (
                                <div className={`grid ${images.length === 3 ? 'grid-cols-2' : 'grid-cols-2'} gap-1.5`}>
                                  {images.slice(0, 4).map((img, idx) => (
                                    <img 
                                      key={idx} 
                                      src={img} 
                                      alt="" 
                                      className={`w-full ${images.length === 3 && idx === 0 ? 'col-span-2 h-60' : 'h-40'} object-cover rounded-xl border border-gray-800/60`}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Actions */}
                          <div className="mt-3 flex items-center gap-1 -ml-2">
                            <button 
                              onClick={() => sharePost(post)}
                              className="group/btn flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-[#a15507]/10 text-gray-500 hover:text-[#a15507] transition-colors" 
                              aria-label="Share"
                            >
                              {copiedPostId === post.id ? <Check size={18} className="text-green-400" /> : <Share2 size={18} className="group-hover/btn:scale-110 transition-transform" />}
                              <span className="text-xs">{copiedPostId === post.id ? t('postsFeed.copied', 'Copied') : t('postsFeed.share', 'Share')}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {pageData.totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-3">
                    <Button
                      variant="outline"
                      onClick={() => goToPage(page - 1)}
                      disabled={page <= 0}
                      className="px-5"
                    >
                      {t('common.previous')}
                    </Button>
                    <div className="flex items-center gap-1.5 px-4 py-2 bg-gray-900/40 rounded-lg border border-gray-800/50">
                      <span className="text-white font-medium">{page + 1}</span>
                      <span className="text-gray-600">/</span>
                      <span className="text-gray-400">{pageData.totalPages}</span>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => goToPage(page + 1)}
                      disabled={page + 1 >= pageData.totalPages}
                      className="px-5"
                    >
                      {t('common.next')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PostsFeed;
